import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Modal from '../modal/Modal';
import { Col, getItemWithExpiration, Row } from '../../lib/utils';
import { Body2Bold, Body2Regular, Body3Regular, CaptionLight, CaptionRegular, Subtitle1Bold } from '../../lib/font';
import useMembershipService from '../../services/useMembershipService';
import CloseButton from '../Button/CloseButton';
import NewColorRoundButton from '../Button/NewColorRoundButton';
import RadiusTextInput from '../TextInput/RadiusTextInput';
import MembershipTemplate from '../../scenes/ClassPass/ClassPassList/MembershipTemplate';
import dayjs from 'dayjs';
import ColTitleAndTextInput from '../ColTitleAndTextInput';
import MemoTextInput from '../TextInput/MemoTextInput';
import NewWhiteSquareButton from '../Button/NewWhiteSquareButton';
import NewColorSquareButton from '../Button/NewColorSquareButton';
import ModalUpdateMembershipName from './ModalUpdateMembershipName';
import { useQueryClient } from '@tanstack/react-query';
import { MembershipTemplateDataType } from '../../services/useMembershipTemplateQueryService';
import {
    MemberShipApiType,
    MembershipCreationResult,
    MembershipPaymentMethod,
    RappoPayBillingMode,
} from '../../api/memberShip';
import Dropdown from '../DropDown/DropDown';
import { colors } from '../../lib/colors';
import useSingleQuery from '../../hook/useSingleQuery';
import useMembershipPaymentsMutationService from '../../services/useMembershipPaymentsMutationService';
import { fetchGetSalesManagers, SalesManager } from '../../services/useMembershipPaymentsQueryService';
import { CodeValueType } from '../../types/types';
import ModalSalesManagerName from './ModalSalesManagerName';
import NewCalendar from '../Calendar/NewCalendar';
import { fetchGetIsNewSales } from '../../services/useMembershipQueryService';
import SvgIcon from '../SvgIcon';
import { BoldText, RegularText } from '../../lib/Text';
import { Check, Smartphone } from 'lucide-react';
import { useTickets, useCreateMemberTicket } from '@/features/groupTicket/hooks';
import { Ticket as GroupTicketType, ExpiryPolicy } from '@/features/groupTicket/types';
import { useCreateSubscription } from '@/features/facilitySubscription/hooks';
import MembershipBundleFacilitySection, { BundleRef } from './MembershipBundleFacilitySection';
import {
    canRetryTemplateShareProvisioning,
    getTemplateShareProvisioning,
    isSharedMembershipTemplate,
    MULTI_WRITE_NOTICE,
    resolveSharePoolAdminError,
    retryTemplateShareProvisioning,
    ShareProvisioningView,
    shouldWarnTemplateShareProvisioning,
} from '../../features/sharedMembership';

const EXPIRY_POLICY_OPTIONS: { value: ExpiryPolicy; label: string; desc: string }[] = [
    { value: 'FIRST', label: '둘 중 빠른 것', desc: '회차/기간 중 먼저 도달' },
    { value: 'COUNT', label: '회차 소진 시', desc: '횟수 다 쓰면 만료' },
    { value: 'PERIOD', label: '기간 만료 시', desc: '기간 끝나면 만료' },
];

const getCreatedMembershipId = (result: MembershipCreationResult) =>
    String(result?.data?.membership?._id || result?.data?.membership?.id || '').trim();

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    // 새로운 수강권 만들기 버튼 클릭 시
    onClickCreateNewMembership: () => void;

    memberName?: string;
    coachName?: string;
    coachId?: string;
    memberId?: string;
    type?: string;
    createAlert?: any;

    // 수강권 등록 완료 시
    onMembershipCreateSuccess?: (membership: MemberShipApiType) => void;

    /** 결제 방식 기본값 (라포페이 정산 > 결제 요청 탭에서 진입 시 'rappoPay'로 설정) */
    defaultPaymentMethod?: MembershipPaymentMethod;
    /** 'group'이면 그룹 수업 수강권(Ticket/MemberTicket) 발급 모드 — 템플릿/API/UI 분기 */
    source?: 'pt' | 'group';
    /** source='group'일 때 필수 — MemberTicket 발급 API의 centerId */
    centerId?: string;
};

const ModalMembershipCreate = ({
    setIsVisible,
    onClickCreateNewMembership,
    memberName,
    coachName,
    coachId,
    memberId,
    type = 'memberProfile',
    createAlert,
    onMembershipCreateSuccess = undefined,
    defaultPaymentMethod,
    source = 'pt',
    centerId: centerIdProp,
}: Props) => {
    const isGroup = source === 'group';
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const queryClient = useQueryClient();

    const showShareProvisioningWarning: (
        membershipId: string,
        provisioning: ShareProvisioningView,
    ) => void = (membershipId, provisioning) => {
        const errorCodeText = provisioning.errorCode
            ? `\n확인 코드: ${provisioning.errorCode}`
            : '';
        const canRetry =
            !!membershipId && canRetryTemplateShareProvisioning(provisioning);
        const title =
            provisioning.status === 'REVIEW_REQUIRED'
                ? '수강권 발급 완료 · 운영 확인 필요'
                : '수강권 발급 완료 · 공유 설정 확인 필요';
        const statusMessage =
            provisioning.status === 'PENDING'
                ? '공유 설정이 아직 처리 중입니다.'
                : provisioning.status === 'FAILED'
                ? '공유 설정 처리에 실패했습니다.'
                : provisioning.status === 'REVIEW_REQUIRED'
                ? '자동 복구를 계속할 수 없습니다. 확인 코드를 포함해 고객센터 또는 운영 담당자에게 문의해주세요.'
                : '공유 설정 상태를 다시 확인해주세요.';
        const retryShareProvisioning = async () => {
            try {
                const nextProvisioning = await retryTemplateShareProvisioning(membershipId);
                if (nextProvisioning.status === 'COMPLETED') {
                    queryClient.invalidateQueries({ queryKey: ['sharedMembership'] });
                    queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
                    createAlert(
                        '공유 설정 완료',
                        '이미 발급된 수강권에 공유 설정을 완료했습니다.',
                        false,
                    );
                    return;
                }
                showShareProvisioningWarning(membershipId, nextProvisioning);
            } catch {
                try {
                    const currentProvisioning = await getTemplateShareProvisioning(membershipId);
                    showShareProvisioningWarning(membershipId, currentProvisioning);
                } catch {
                    createAlert(
                        '공유 설정 상태 확인 실패',
                        '수강권은 정상 발급되어 있습니다. 수강권을 다시 발급하지 말고 수강권 상세에서 공유 설정 상태를 확인해주세요.',
                        true,
                    );
                }
            }
        };

        const message = `수강권과 결제 정보는 정상 발급되었습니다.\n${statusMessage}\n수강권 전체를 다시 발급하지 마세요.${errorCodeText}`;
        if (!canRetry) {
            createAlert(title, message, true, '확인', undefined);
            return;
        }
        // 재시도 실패는 이 경고를 다시 띄우므로, 재시도 외에 닫을 방법이 없으면
        // 공유 설정이 계속 실패할 때 관리자가 알럿에서 빠져나올 수 없다.
        // 왼쪽 '닫기'로 항상 탈출 가능하게 두고, 재시도는 오른쪽 버튼으로 둔다.
        createAlert(
            title,
            message,
            true,
            '공유 설정 재시도',
            () => void retryShareProvisioning(),
            true,
            '닫기',
            undefined,
        );
    };

    const { allMembershipTemplatesListData, createMemberShipMutation } = useMembershipService({
        fetchAllMembershipTemplatesList: !isGroup,
        onCreateMemberShipSuccess(result) {
            const isRappoPay = paymentMethod === 'rappoPay';
            const shareProvisioning = result?.data?.shareProvisioning;
            const createdMembershipId = getCreatedMembershipId(result);
            setIsVisible(false);
            if (
                shareProvisioning &&
                shouldWarnTemplateShareProvisioning(shareProvisioning)
            ) {
                showShareProvisioningWarning(createdMembershipId, shareProvisioning);
            } else if (isRappoPay) {
                createAlert(
                    '라포페이 결제 요청 안내',
                    '심사 통과후 라포페이로 수강권 등록시\n다음 페이지가 포함된 알림톡이\n회원에게 전송될 예정이에요!\n\n라포페이 결제링크\nhttps://www.rappomanager.com/pay/demo-prepay-001',
                    false,
                    '결제링크 열기',
                    () => window.open('https://www.rappomanager.com/pay/demo-prepay-001', '_blank')
                );
            } else if (shareProvisioning?.status === 'COMPLETED') {
                createAlert(
                    '수강권 등록 완료',
                    '수강권 발급과 공유 설정이 모두 완료되었습니다!',
                    false,
                );
            } else {
                createAlert('수강권 등록 완료', '새로운 수강권이 성공적으로 등록되어 회원님께 발급되었습니다!', false);
            }
            if (type === 'memberProfile') {
                queryClient.invalidateQueries({
                    queryKey: ['getAllMembershipForMemberQuery'],
                });
                queryClient.invalidateQueries({
                    queryKey: ['getCenterMemberListAPIQuery'],
                });
            } else if (type === 'schedule') {
                // 수강권 데이터 다시 불러와야함
                queryClient.invalidateQueries({
                    queryKey: ['getMemberListForCenterAPI'],
                });
            }
        },
        onCreateMemberShipFail(result) {
            // 공유 상품 발급 검증(409)은 Membership 을 쓰기 전에 돌아 실패 시 아무것도 만들어지지 않는다.
            // 모달은 그대로 두고 원인만 알린다 — 입력을 고쳐 바로 다시 시도할 수 있어야 한다.
            const resolved = resolveSharePoolAdminError(
                result,
                '수강권을 발급하지 못했습니다. 잠시 후 다시 시도해주세요.',
            );
            createAlert('수강권 등록 실패', resolved.message, true);
        },
    });

    // 그룹 수강권(Ticket) 목록 + 발급 mutation
    const groupCenterId = centerIdProp ?? authObject?.centerId;
    const { data: groupTicketsRaw = [] } = useTickets({ centerId: groupCenterId, enabled: isGroup && !!groupCenterId });
    const createMemberTicketMutation = useCreateMemberTicket();

    // 발급 요청 진행 중 여부 — 버튼 disabled 표시용이다. state라 리렌더 전에는 갱신되지
    // 않으므로 중복 발급 차단은 아래 submitLockRef가 담당한다.
    const isSubmitting = createMemberShipMutation.isLoading || createMemberTicketMutation.isLoading;

    // 같은 틱에 도착한 연타를 동기적으로 막는 잠금. 실제 요청이 나간 경우에만 유지되고
    // mutation settled에서 풀린다.
    const submitLockRef = useRef(false);
    const releaseSubmitLock = () => {
        submitLockRef.current = false;
    };

    // 그룹 Ticket을 MembershipTemplate shape로 매핑 — 카드 렌더/선택 로직 재사용
    const groupTemplates = useMemo(
        () =>
            (groupTicketsRaw as GroupTicketType[]).map((t) => {
                const totalCount = t.totalCount ?? 0;
                const pricePerSchedule =
                    t.perSessionFee ??
                    (t.expiryPolicy !== 'PERIOD' && totalCount > 0 ? Math.round(t.price / totalCount) : 0);
                return {
                    _id: t._id,
                    name: t.name,
                    totalSession: totalCount,
                    totalPrice: t.price,
                    pricePerSchedule,
                    validDays: t.periodDays ?? 0,
                    period: 0, // 그룹은 수업시간 개념 없음
                    BGColor: ['#10B981', '#065F46'], // 그룹 에메랄드 팔레트
                    isAvailable: t.isActive,
                    issuer: { issuerRole: 'admin' }, // MembershipTemplate 카드 컴포넌트 호환
                    __groupTicket: t, // 원본 참조
                };
            }),
        [groupTicketsRaw],
    );

    const { postSalesManagerMutation } = useMembershipPaymentsMutationService({
        onPostSalesManagerSuccess(data) {
            setSalesManager(resfineSalesManagers(data));
        },
    });
    const { refinedResult, isLoading, isFetching } = useSingleQuery({
        fetchFn: fetchGetSalesManagers,
        params: { centerId: authObject.centerId },
        enabled: !!authorization,
        suspense: false,
    });
    const { refinedResult: _result } = useSingleQuery({
        fetchFn: fetchGetIsNewSales,
        params: { memberId: memberId, coachId: coachId },
        enabled: !!authorization && !isGroup && !!coachId && !!memberId,
        suspense: false,
    });

    // userId, userName -> code, value
    // 마지막에 직접입력 추가
    const resfineSalesManagers = useCallback(
        (data: any): Array<{ code: string; value: string; onClick?: () => void }> => {
            // userId, userName -> code, value 변환
            const _salesManagers = Array.isArray(data)
                ? data.map((item: any) => ({
                      code: item.userId,
                      value: item.userName,
                      object: item,
                  }))
                : [];

            // 직접입력 추가
            return [
                ..._salesManagers,
                {
                    code: 'direct',
                    value: '직접입력',
                    onClick: () => setIsSalesManagerModalVisible(true),
                },
            ];
        },
        []
    );
    const [salesManager, setSalesManager] = useState<CodeValueType[] | undefined>(undefined);

    useEffect(() => {
        if (!isLoading && !isFetching) {
            setSalesManager(resfineSalesManagers(refinedResult.data));
        }
    }, [isLoading, isFetching]);

    // 판매담당자 추가 모달
    const [isSalesManagerModalVisible, setIsSalesManagerModalVisible] = useState(false);
    // 결제일자 캘린더
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isMemberTypeModalVisible, setIsMemberTypeModalVisible] = useState(false);
    // 필수 입력값 에러 여부
    const [isRequiredError, setIsRequiredError] = useState<boolean>(false);

    // 시설 이용권 번들 발급 (관리자 PC 웹 전용)
    // - onMembershipCreateSuccess 경로는 회원이 아직 서버에 생성되기 전 드래프트 수집 플로우(MemberTemporaryPage)라 미지원.
    // - memberId + effectiveCenterId 있어야 함.
    const effectiveCenterId = centerIdProp ?? authObject?.centerId;
    const canBundleFacility = !!memberId && !!effectiveCenterId && !onMembershipCreateSuccess;
    const bundleRef = useRef<BundleRef>(null);
    const createSubscriptionMutation = useCreateSubscription();

    const fireBundleSubscriptionsIfEnabled = useCallback(() => {
        if (!canBundleFacility) return;
        if (!bundleRef.current?.isEnabled()) return;
        const items = bundleRef.current.getBodies();
        if (items.length === 0 || !memberId || !effectiveCenterId) return;

        const subscriptionMutateAsync = createSubscriptionMutation.mutateAsync;
        Promise.allSettled(
            items.map((it) =>
                subscriptionMutateAsync({
                    centerId: effectiveCenterId,
                    memberId,
                    body: it.body,
                }).then(
                    () => ({ ok: true as const, name: it.productName }),
                    () => ({ ok: false as const, name: it.productName }),
                ),
            ),
        ).then((results) => {
            const failed = results
                .map((r) => (r.status === 'fulfilled' ? r.value : { ok: false as const, name: '(알 수 없음)' }))
                .filter((v) => !v.ok)
                .map((v) => v.name);
            if (failed.length > 0) {
                const list = failed.map((n) => `· ${n}`).join('\n');
                createAlert?.(
                    '일부 처리 실패',
                    `수강권은 정상 발급됐으나 다음 이용권 발급에 실패했어요:\n${list}\n\n실패한 이용권은 별도로 다시 발급해주세요.`,
                    true,
                );
            }
        });
    }, [canBundleFacility, memberId, effectiveCenterId, createSubscriptionMutation, createAlert]);

    const [filterText, setFilterText] = useState<string>('');

    // filterText 가 있을 때만 필터링 — source에 따라 PT 템플릿 or 그룹 Ticket
    const templateDataSource = isGroup ? groupTemplates : allMembershipTemplatesListData;
    const filteredData = templateDataSource.filter(
        (item: any) => item.name.includes(filterText) && item.isAvailable === true
    );

    // 수강권 이름 수정 모달
    const [isMembershipNameModalVisible, setIsMembershipNameModalVisible] = useState(false);

    // 1,2 단계
    const [step, setStep] = useState<number>(1);
    // 수강권 템플릿 Id
    const [selectMemberShipTemplateId, setSelectMemberShipTemplateId] = useState<string>('');
    // 고른 상품이 공유 상품인지. 발급 즉시 공유 풀이 열리므로 2단계에서 제약을 미리 알린다.
    const [isSharedTemplateSelected, setIsSharedTemplateSelected] = useState<boolean>(false);

    // 회당 가격
    const [pricePerClass, setPricePerClass] = useState<number>(0);
    // 판매 금액
    const [totalPrice, setTotalPrice] = useState<number>(0);

    // 시작 날짜
    const [startDate, setStartDate] = useState<string>('');
    // 만료 날짜 expirationDate
    const [expirationDate, setExpirationDate] = useState<string>('');
    // 수강권 기간
    const [membershipPeriod, setMembershipPeriod] = useState<number>(0);
    // 시작 회차 startSession
    const [startSession, setStartSession] = useState<number>(1);
    // 총 회차 totalSession
    const [totalSession, setTotalSession] = useState<number>(0);
    // 만료 정책 (그룹 수강권 전용 — 템플릿 기본값에서 override 가능)
    const [expiryPolicy, setExpiryPolicy] = useState<ExpiryPolicy>('FIRST');
    // 취소 정책 override (그룹 수강권 전용)
    const [useCustomCancel, setUseCustomCancel] = useState(false);
    const [cancellableCountOverride, setCancellableCountOverride] = useState<number | undefined>(undefined);
    const [sameDayCancelCountOverride, setSameDayCancelCountOverride] = useState<number | undefined>(undefined);
    // 수업 시간
    const [classTime, setClassTime] = useState<number>(50);
    // 메모
    const [memo, setMemo] = useState<string>('');
    // BGColor
    const [BGColor, setBGColor] = useState<string[]>([]);
    // 수강권 이름
    const [membershipName, setMembershipName] = useState<string>('');
    // 결제일자
    const [paymentDate, setPaymentDate] = useState<string>(dayjs().toISOString());
    // 판매담당자
    const [selectedSalesManager, setSelectedSalesManager] = useState<SalesManager | undefined>(undefined);
    // 회원 구분
    const [memberType, setMemberType] = useState<string>('none'); // none, new, re

    // 결제 방식: 현장결제 | 라포페이 결제요청 (백엔드 미연동 시 UI·페이로드만 유지)
    const [paymentMethod, setPaymentMethod] = useState<MembershipPaymentMethod>('inPerson');
    /** 라포페이 선택 시: 즉시 선결제(prepaid) | 수업당 결제(perClass) */
    const [rappoPayBillingMode, setRappoPayBillingMode] = useState<RappoPayBillingMode>('prepaid');
    const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);

    // 분할 결제 여부
    const [isSplitPayment, setIsSplitPayment] = useState<boolean>(false);
    // 결제 금액
    const [paymentAmount, setPaymentAmount] = useState<number>(0);
    // 미수금액
    const [unpaidAmount, setUnpaidAmount] = useState<number>(0);
    // 라포페이 버튼 앵커 좌표
    const rappoPayButtonRef = useRef<HTMLButtonElement | null>(null);
    const [rappoPayAnchorRect, setRappoPayAnchorRect] = useState<{
        top: number;
        left: number;
        right: number;
        bottom: number;
        width: number;
        height: number;
    } | null>(null);

    // trial 계정만 현장/라포페이 분기 UI 노출 (라이브 점진 배포용) — 그룹 수강권은 라포페이 미지원
    const isPaymentMethodBranchEnabled = authObject?.adminId === 'trial' && !isGroup;
    const effectivePaymentMethod: MembershipPaymentMethod = isPaymentMethodBranchEnabled
        ? paymentMethod
        : 'inPerson';

    // 선택된 그룹 Ticket 템플릿 원본 (취소 정책 기본값 placeholder용)
    const selectedGroupTicket = useMemo(() => {
        if (!isGroup || !selectMemberShipTemplateId) return undefined;
        const match = (groupTemplates as any[]).find((t) => t._id === selectMemberShipTemplateId);
        return match?.__groupTicket as GroupTicketType | undefined;
    }, [isGroup, groupTemplates, selectMemberShipTemplateId]);
    // 담당자 초기값 , coach id
    const initialSalesManager = salesManager?.find((item) => item.code === coachId);

    // selectedSalesManager가 비어있고 salesManager가 로드되면 초기값으로 동기화
    // (Dropdown은 initialSelected만 표시할 뿐 onSelect 없이 state는 undefined로 남음)
    useEffect(() => {
        if (selectedSalesManager || !salesManager || salesManager.length === 0) return;
        const initial = (initialSalesManager?.object as SalesManager | undefined) ??
            (salesManager[0] as any).object;
        if (initial) setSelectedSalesManager(initial);
    }, [salesManager, initialSalesManager, selectedSalesManager]);

    // 수강권 선택
    const onClickMembershipTemplate = (item: MembershipTemplateDataType) => {
        setStep(2);
        setPricePerClass(item.pricePerSchedule);
        setTotalPrice(item.totalPrice);
        setStartDate(dayjs().toISOString());
        setExpirationDate(dayjs().add(item.validDays, 'day').toISOString());
        setMembershipPeriod(item.validDays);
        setTotalSession(item.totalSession);
        setBGColor(item.BGColor);
        setMembershipName(item.name);
        setSelectMemberShipTemplateId(item._id);
        setIsSharedTemplateSelected(isSharedMembershipTemplate(item));
        setClassTime(item.period);
        // expiryPolicy 초기화
        if (isGroup) {
            // 그룹: 템플릿의 expiryPolicy로 초기화 (없으면 FIRST)
            const groupTicket = (item as any).__groupTicket as GroupTicketType | undefined;
            setExpiryPolicy(groupTicket?.expiryPolicy ?? 'FIRST');
            // 취소 override 리셋 (템플릿이 바뀌면 기본값 사용으로 복귀)
            setUseCustomCancel(false);
            setCancellableCountOverride(undefined);
            setSameDayCancelCountOverride(undefined);
        } else {
            // PT: 레거시 기본값 COUNT
            setExpiryPolicy('COUNT');
        }
        if (defaultPaymentMethod === 'rappoPay') {
            setPaymentMethod('rappoPay');
            setIsPreviewVisible(true);
            setIsSplitPayment(false);
            // 라포페이 결제시 기간 최대 6개월(180일) 기본값 적용
            const rappoPayDays = Math.min(item.validDays, 180);
            setMembershipPeriod(rappoPayDays);
            setExpirationDate(dayjs().add(rappoPayDays, 'day').toISOString());
        } else {
            setPaymentMethod('inPerson');
        }
    };

    /**
     * 실제 발급 요청을 보냈거나 상위 화면으로 넘겼으면 true, 입력 검증에서 멈췄으면 false.
     * 반환값으로 제출 잠금을 유지할지 즉시 풀지 결정한다.
     */
    const startMembershipCreation = (): boolean => {
        // PT만 수업시간 검증 (그룹은 수업시간 개념 없음)
        if (!isGroup) {
            if (classTime === null || classTime.toString().trim().length < 1) {
                createAlert('필수 입력', '수업 시간을 입력하여 주세요', true);
                return false;
            }
            if (classTime % 10 > 0 || classTime.toString().trim().length < 1) {
                createAlert('올바르지 않은 입력', '수업 시간을 10분단위로 입력하여 주세요', true);
                return false;
            }
        }
        // 회원 구분이 code 까 none일경우
        if (memberType === 'none') {
            setIsMemberTypeModalVisible(true);
            setIsRequiredError(true);
            return false;
        }

        // 시설 이용권 번들 입력 검증 (활성화된 경우만 실제 검증, 아니면 통과)
        if (canBundleFacility && bundleRef.current && !bundleRef.current.validate()) {
            return false;
        }

        // 판매담당자는 PT·그룹·임시회원 세 분기가 모두 요구한다. 분기마다 조용히 return하면
        // "등록 완료" 버튼이 무반응으로 보이므로 한 곳에서 알럿과 함께 막는다.
        if (!selectedSalesManager) {
            createAlert('필수 입력', '판매 담당자를 선택해주세요', true);
            return false;
        }

        // 그룹 수강권 발급 — MemberTicket 엔드포인트로 분기
        if (isGroup) {
            if (!memberId || !groupCenterId) {
                createAlert('필수 입력', '회원/센터 정보가 없습니다', true);
                return false;
            }
            // 취소 정책 override validation
            if (useCustomCancel
                && cancellableCountOverride != null && sameDayCancelCountOverride != null
                && sameDayCancelCountOverride > cancellableCountOverride) {
                createAlert('입력 오류', '당일 취소 횟수는 총 취소 횟수보다 클 수 없습니다.', true);
                return false;
            }
            createMemberTicketMutation.mutate(
                {
                    centerId: groupCenterId,
                    memberId,
                    body: {
                        ticketId: selectMemberShipTemplateId,
                        startDate,
                        price: totalPrice,
                        perSessionFee: pricePerClass,
                        isNewSales: memberType === 'new',
                        isSplitPayment,
                        // 분할결제 시 첫 납입액 — 서버(memberTicket.service.ts)가 없으면 price 전액으로 처리
                        ...(isSplitPayment ? { totalPaymentAmount: paymentAmount } : {}),
                        salesManager: selectedSalesManager,
                        paymentMethod: 'card', // 현장결제 기본 — UI에서 추후 확장 가능
                        paymentDate: dayjs(paymentDate).toISOString(),
                        // 만료 정책 override (백엔드: body.expiryPolicy → ticket.expiryPolicy → 'FIRST')
                        expiryPolicy,
                        // 회차/기간 override (백엔드: body 값 → ticket 값 상속)
                        totalCount: totalSession > 0 ? totalSession : undefined,
                        periodDays: membershipPeriod > 0 ? membershipPeriod : undefined,
                        // 시작 회차 — 폼 입력값 그대로 (UI 디폴트 1)
                        startSessionValue: startSession,
                        // 취소 정책 override — useCustomCancel ON일 때만 전송 (미전송 시 Ticket 템플릿 값 상속)
                        ...(useCustomCancel ? {
                            cancellableCount: cancellableCountOverride ?? undefined,
                            sameDayCancelCount: sameDayCancelCountOverride ?? undefined,
                        } : {}),
                    },
                },
                {
                    onSuccess: () => {
                        // 수강권 발급 성공 — 번들 이용권은 fire-and-forget (실패해도 수강권은 유지)
                        fireBundleSubscriptionsIfEnabled();
                        setIsVisible(false);
                        createAlert('수강권 등록 완료', '수업 수강권이 성공적으로 발급되었습니다!', false);
                        queryClient.invalidateQueries({ queryKey: ['groupTicket'] });
                    },
                    onError: (err: any) => {
                        createAlert('등록 실패', err?.serverMessage || '수강권 발급에 실패했습니다', true);
                    },
                    onSettled: releaseSubmitLock,
                },
            );
            return true;
        }

        if (onMembershipCreateSuccess) {
            const membership: MemberShipApiType = {
                coachId: coachId,
                membershipTemplateId: selectMemberShipTemplateId,
                name: membershipName,
                period: classTime,
                activationDate: startDate,
                expirationDate: expirationDate,
                startSessionValue: startSession,
                totalSession: totalSession,
                memo: memo,
                pricePerSchedule: pricePerClass,
                status: 'active',
                totalPrice: totalPrice,
                BGColor: BGColor,
                issuer: { issuerId: authObject.adminId, issuerRole: 'admin' },
                salesManager: selectedSalesManager,
                isNewSales: memberType === 'new' ? true : false,
                isSplitPayment: isSplitPayment,
                totalPaymentAmount: paymentAmount,
                initialPaymentDate: dayjs(paymentDate).toISOString(),
                expiryPolicy,
                // TODO: 백엔드 완성 후 주석 해제
                // paymentMethod: effectivePaymentMethod,
                // ...(effectivePaymentMethod === 'rappoPay' ? { rappoPayBillingMode } : {}),
            };

            // 상위 화면이 목록에 추가하고 모달을 닫는다. 연타로 같은 수강권이 두 번
            // 들어가지 않도록 잠금은 유지한 채 넘긴다(모달이 닫히며 ref도 함께 사라진다).
            onMembershipCreateSuccess(membership);
            return true;
        }

        createMemberShipMutation.mutate(
            {
                coachId: coachId,
                memberId: memberId,
                    membershipTemplateId: selectMemberShipTemplateId,
                name: membershipName,
                period: classTime,
                activationDate: startDate,
                expirationDate: expirationDate,
                startSessionValue: startSession,
                totalSession: totalSession,
                memo: memo,
                pricePerSchedule: pricePerClass,
                status: 'active', // [ ] active 의 기준?
                totalPrice: totalPrice,
                issuer: { issuerId: authObject.adminId, issuerRole: 'admin' },
                BGColor: BGColor,
                salesManager: selectedSalesManager,
                isNewSales: memberType === 'new' ? true : false,
                initialPaymentDate: dayjs(paymentDate).toISOString(),
                isSplitPayment: isSplitPayment,
                totalPaymentAmount: paymentAmount,
                expiryPolicy,
                // TODO: 백엔드 완성 후 주석 해제
                // paymentMethod: effectivePaymentMethod,
                // ...(effectivePaymentMethod === 'rappoPay' ? { rappoPayBillingMode } : {}),
                accessToken: authorization,
            },
            {
                // 수강권 발급 성공 시 번들 이용권도 fire-and-forget (실패해도 수강권은 유지).
                // 201 이 아니면 수강권이 없으므로 묶음 이용권만 따로 발급되면 안 된다.
                onSuccess: (result) => {
                    if (result?.status === 201) fireBundleSubscriptionsIfEnabled();
                },
                onSettled: releaseSubmitLock,
            },
        );
        return true;
    };

    // 등록완료 버튼 클릭 시
    const onClickCreateMembership = () => {
        // isSubmitting은 state라 리렌더 전에 도착한 연타를 막지 못한다(1ms 간격 중복 발급 재현).
        // 같은 틱에 ref로 선점하고, 요청이 나가지 않았으면 곧바로 되돌린다.
        if (submitLockRef.current || isSubmitting) return;
        submitLockRef.current = true;

        let submitted = false;
        try {
            submitted = startMembershipCreation();
        } finally {
            if (!submitted) releaseSubmitLock();
        }
    };

    // 수강권 기간 계산, 만료날짜 - 시작날짜
    useEffect(() => {
        if (startDate && expirationDate) {
            const start = dayjs(startDate);
            const end = dayjs(expirationDate);
            const diff = end.diff(start, 'days');
            setMembershipPeriod(diff);
        }
    }, [startDate, expirationDate]);

    // 미수금액 = 판매금액 - 결제금액
    useEffect(() => {
        if (isSplitPayment) {
            setUnpaidAmount(totalPrice - paymentAmount);
        }
    }, [paymentAmount, totalPrice, isSplitPayment]);

    // 스크롤 시 미리보기 숨김 (모달 내부/외부 스크롤 모두 포함)
    useEffect(() => {
        if (!(isPaymentMethodBranchEnabled && paymentMethod === 'rappoPay' && step === 2 && isPreviewVisible))
            return;

        const hidePreviewOnScroll = () => {
            setIsPreviewVisible(false);
        };

        document.addEventListener('scroll', hidePreviewOnScroll, true);
        return () => {
            document.removeEventListener('scroll', hidePreviewOnScroll, true);
        };
    }, [isPaymentMethodBranchEnabled, paymentMethod, step, isPreviewVisible]);

    // 라포페이 화면이 아니면 미리보기 자동 숨김
    useEffect(() => {
        if (paymentMethod !== 'rappoPay' || step !== 2) {
            setIsPreviewVisible(false);
        }
    }, [paymentMethod, step]);

    // 라포페이 결제요청 버튼 위치 추적 (리사이즈/스크롤 대응)
    useEffect(() => {
        if (!(isPaymentMethodBranchEnabled && paymentMethod === 'rappoPay' && step === 2)) return;

        const updateAnchorRect = () => {
            const rect = rappoPayButtonRef.current?.getBoundingClientRect();
            if (!rect) return;
            setRappoPayAnchorRect({
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
            });
        };

        updateAnchorRect();
        window.addEventListener('resize', updateAnchorRect);
        window.addEventListener('scroll', updateAnchorRect, true);

        return () => {
            window.removeEventListener('resize', updateAnchorRect);
            window.removeEventListener('scroll', updateAnchorRect, true);
        };
    }, [isPaymentMethodBranchEnabled, paymentMethod, step]);

    return (
        <Modal whiteViewHeight={700} whiteViewWidth={1080} setIsVisible={setIsVisible} isCloseOutside={false}>
            <CloseButton size={24} className=" absolute right-[25px] top-[25px]" onClick={() => setIsVisible(false)} />
            <Col className="flex py-[36px] px-[48px]">
                <Row className="mb-[24px]">
                    <Subtitle1Bold>수강권 발급하기</Subtitle1Bold>
                </Row>
                {step === 1 ? (
                    <>
                        <Row className="flex justify-between mb-[40px]">
                            <Row className="flex flex-row  items-center">
                                <Col className=" justify-center items-center bg-primary w-[24px] h-[24px] rounded-full">
                                    <CaptionRegular className="text-white">1</CaptionRegular>
                                </Col>
                                <Col className="w-[36px] h-[1px] bg-BLUE_200" />

                                <Col className=" justify-center items-center bg-BLUE_200 w-[24px] h-[24px] rounded-full">
                                    <CaptionRegular className="text-white">2</CaptionRegular>
                                </Col>
                            </Row>
                            <Row>
                                <NewColorRoundButton
                                    bgColor="primary"
                                    text="새로운 수강권 만들기"
                                    onClick={onClickCreateNewMembership}
                                    textColor="white"
                                    fontType="Body3Regular"
                                    width={188}
                                    height={38}
                                    className="mr-[16px]"
                                />
                                <RadiusTextInput
                                    textInputHeight={36}
                                    value={filterText}
                                    placeholder="수강권 검색하기"
                                    width={310}
                                    onChange={(value) => setFilterText(value)}
                                    onClear={() => setFilterText('')}
                                    isRightIcon={false}
                                    isLeftIcon
                                    isEnterChange
                                />
                            </Row>
                        </Row>
                        <div className="grid grid-cols-3 gap-6 w-full px-[56px]">
                            {filteredData.map((item: { _id: any }, index: any) => (
                                <MembershipTemplate
                                    key={item._id}
                                    index={index}
                                    item={item}
                                    onClickDetailPage={onClickMembershipTemplate}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <Row className="flex flex-row  items-center mb-[40px]">
                            <Col className=" justify-center items-center bg-BLUE_200 w-[24px] h-[24px] rounded-full">
                                <CaptionRegular className="text-white">1</CaptionRegular>
                            </Col>
                            <Col className="w-[36px] h-[1px] bg-BLUE_200" />

                            <Col className=" justify-center items-center bg-primary w-[24px] h-[24px] rounded-full">
                                <CaptionRegular className="text-white">2</CaptionRegular>
                            </Col>
                        </Row>
                        <Row className="mb-[32px]">
                            <Col className="mr-[40px]">
                                <Col
                                    style={{
                                        width: 264,
                                        height: 120,
                                        background: `linear-gradient(135deg, ${BGColor[0]} 0%, ${BGColor[1]} 100%)`,
                                        borderRadius: 8,
                                        justifyContent: 'center',
                                        paddingRight: 24,
                                        paddingLeft: 24,
                                        marginBottom: 24,
                                    }}
                                >
                                    <Body2Bold className="text-white">{`${membershipName}`}</Body2Bold>
                                </Col>
                                {isSharedTemplateSelected && (
                                    <Col
                                        style={{
                                            width: 264,
                                            marginTop: -12,
                                            marginBottom: 24,
                                            padding: '10px 12px',
                                            borderLeft: '3px solid #F0C36D',
                                            backgroundColor: '#FFF8E6',
                                            borderRadius: 8,
                                        }}
                                    >
                                        <CaptionRegular
                                            style={{ color: '#8A5A00', lineHeight: '18px', whiteSpace: 'pre-line' }}
                                        >
                                            {`공유 상품입니다. 발급과 동시에 공유가 시작되고, 함께 쓸 회원은 발급 후 수강권 상세에서 추가합니다.\n\n${MULTI_WRITE_NOTICE}`}
                                        </CaptionRegular>
                                    </Col>
                                )}
                                {/* <NewWhiteRoundButton
                                    text="수강권 이름 수정하기"
                                    onClick={() => setIsMembershipNameModalVisible(true)}
                                    fontType="CaptionRegular"
                                    width={136}
                                    height={24}
                                    mb={52}
                                /> */}
                                {coachName && (
                                    <ColTitleAndTextInput
                                        title="담당 강사"
                                        value={coachName}
                                        onChange={() => {}}
                                        width={240}
                                        height={36}
                                        textInputRightIcon={false}
                                        textInputLeftIcon={false}
                                        disabled
                                        mb={40}
                                    />
                                )}
                                {memberName && (
                                    <ColTitleAndTextInput
                                        title="회원"
                                        value={memberName}
                                        onChange={() => {}}
                                        width={240}
                                        height={36}
                                        textInputRightIcon={false}
                                        textInputLeftIcon={false}
                                        disabled
                                    />
                                )}
                            </Col>
                            <Col className="w-[644px] px-[32px] pb-[32px]">
                                {isPaymentMethodBranchEnabled && (
                                    <>
                                <Col className="mb-[24px]">
                                    <Body3Regular className="mb-[12px]">결제 방식</Body3Regular>
                                    <div
                                        className="grid grid-cols-2 gap-[12px]"
                                        role="radiogroup"
                                        aria-label="결제 방식 선택"
                                    >
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked={paymentMethod === 'inPerson'}
                                            onClick={() => {
                                                setPaymentMethod('inPerson');
                                                setIsPreviewVisible(false);
                                                setRappoPayBillingMode('prepaid');
                                            }}
                                            className={`flex flex-col items-start text-left rounded-[12px] p-[14px] transition-colors ${
                                                paymentMethod === 'inPerson'
                                                    ? 'border-2 border-primary bg-BLUE_50'
                                                    : 'border border-solid border-[#D4DBE8] bg-white hover:bg-GREY_50'
                                            }`}
                                        >
                                            <Row className="items-center mb-[8px] w-full">
                                                <SvgIcon
                                                    name="SvgCreditCard"
                                                    size={28}
                                                    fill={colors.primary}
                                                    className="shrink-0 mr-[8px]"
                                                />
                                                <Body2Bold className="text-[#1A305D]">현장결제</Body2Bold>
                                                {paymentMethod === 'inPerson' && (
                                                    <Col className="ml-auto flex shrink-0 items-center justify-center">
                                                        {/* SvgCheck는 viewBox 12×8이라 size 정사각형 사용 시 가로로 눌림 → lucide Check 사용 */}
                                                        <Check
                                                            size={20}
                                                            className="text-primary"
                                                            strokeWidth={2.5}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                            <CaptionLight className="text-WHITE_900 leading-[18px]">
                                                현장에서 결제하는 수강권이에요
                                            </CaptionLight>
                                        </button>
                                        <button
                                            ref={rappoPayButtonRef}
                                            type="button"
                                            role="radio"
                                            aria-checked={paymentMethod === 'rappoPay'}
                                            onClick={() => {
                                                setPaymentMethod('rappoPay');
                                                setIsPreviewVisible(true);
                                                setIsSplitPayment(false);
                                            }}
                                            className={`flex flex-col items-start text-left rounded-[12px] p-[14px] transition-colors ${
                                                paymentMethod === 'rappoPay'
                                                    ? 'border-2 border-primary bg-BLUE_50'
                                                    : 'border border-solid border-[#D4DBE8] bg-white hover:bg-GREY_50'
                                            }`}
                                        >
                                            <Row className="items-center mb-[8px] w-full">
                                                <Col className="shrink-0 mr-[8px] text-primary">
                                                    <Smartphone size={28} strokeWidth={1.75} />
                                                </Col>
                                                <Body2Bold className="text-[#1A305D]">라포페이 결제요청</Body2Bold>
                                                {paymentMethod === 'rappoPay' && (
                                                    <Col className="ml-auto flex shrink-0 items-center justify-center">
                                                        <Check
                                                            size={20}
                                                            className="text-primary"
                                                            strokeWidth={2.5}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                            <CaptionLight className="text-WHITE_900 leading-[18px]">
                                                회원님의 휴대폰으로 바로 결제 가능한 온라인 결제 방식이에요
                                                하단의 &apos;등록 완료&apos; 버튼을 누르면 결제요청 카카오 알림톡이 전송돼요
                                            </CaptionLight>
                                        </button>
                                    </div>
                                </Col>
                                {paymentMethod === 'rappoPay' && (
                                    <Col className="mb-[24px]">
                                        <Body3Regular className="mb-[12px]">라포페이 결제방식</Body3Regular>
                                        <div
                                            className="grid grid-cols-2 gap-[12px]"
                                            role="radiogroup"
                                            aria-label="라포페이 결제방식 선택"
                                        >
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked={rappoPayBillingMode === 'prepaid'}
                                                onClick={() => setRappoPayBillingMode('prepaid')}
                                                className={`flex flex-col items-start text-left rounded-[12px] p-[14px] transition-colors ${
                                                    rappoPayBillingMode === 'prepaid'
                                                        ? 'border-2 border-primary bg-BLUE_50'
                                                        : 'border border-solid border-[#D4DBE8] bg-white hover:bg-GREY_50'
                                                }`}
                                            >
                                                <Body2Bold className="text-[#1A305D] mb-[8px]">기존 선결제</Body2Bold>
                                                <CaptionLight className="text-WHITE_900 leading-[18px]">
                                                    기존 방식으로 수강권의 모든 회차에 대한 비용을 미리 내는 방식입니다
                                                </CaptionLight>
                                            </button>
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked={rappoPayBillingMode === 'perClass'}
                                                onClick={() => setRappoPayBillingMode('perClass')}
                                                className={`flex flex-col items-start text-left rounded-[12px] p-[14px] transition-colors ${
                                                    rappoPayBillingMode === 'perClass'
                                                        ? 'border-2 border-primary bg-BLUE_50'
                                                        : 'border border-solid border-[#D4DBE8] bg-white hover:bg-GREY_50'
                                                }`}
                                            >
                                                <Body2Bold className="text-[#1A305D] mb-[8px]">수업당 결제</Body2Bold>
                                                <CaptionLight className="text-WHITE_900 leading-[18px]">
                                                    회원이 결제수단을 등록해놓은 후 수업 종료 시점마다 해당 수업료가 결제되는 방식입니다
                                                </CaptionLight>
                                            </button>
                                        </div>
                                    </Col>
                                )}
                                    </>
                                )}
                                {effectivePaymentMethod !== 'rappoPay' && (
                                    <Row className=" items-center mb-[17px]">
                                        <Col
                                            onClick={() => setIsSplitPayment(!isSplitPayment)}
                                            className={` justify-center items-center w-[24px] h-[24px] rounded-[3px] cursor-pointer mr-[7px] ${
                                                isSplitPayment
                                                    ? 'bg-primary'
                                                    : 'white border-solid border-[#D4DBE8] border-[2px]'
                                            }`}
                                        >
                                            {isSplitPayment && (
                                                <SvgIcon
                                                    name="SvgCheck"
                                                    size={13}
                                                    fill={colors.trans}
                                                    stroke="white"
                                                    strokeWidth={2}
                                                />
                                            )}
                                        </Col>
                                        <RegularText fontSize={14} lineHeight={22} color="#1A305D">
                                            분할 결제
                                        </RegularText>
                                    </Row>
                                )}
                                <Col className="w-full h-[1px] bg-[#D9D9D9] mb-[29px]" />
                                {isSplitPayment && (
                                    <Row className="mb-[34px]">
                                        <ColTitleAndTextInput
                                            title="결제 금액"
                                            numeric
                                            isMoney
                                            allowDecimal
                                            value={paymentAmount.toString()}
                                            onChange={(value) => {
                                                if (value === '') {
                                                    value = '0';
                                                }
                                                setPaymentAmount(parseInt(value));
                                            }}
                                            width={240}
                                            height={36}
                                            onClear={() => {
                                                setPaymentAmount(0);
                                            }}
                                            textInputRightIcon={false}
                                            isClearIcon={false}
                                            mr={60}
                                        />
                                        <ColTitleAndTextInput
                                            title="미수금액"
                                            numeric
                                            isMoney
                                            allowDecimal
                                            value={unpaidAmount.toString()}
                                            onChange={(value) => {}}
                                            width={240}
                                            height={36}
                                            onClear={() => {}}
                                            textInputRightIcon={false}
                                            isClearIcon={false}
                                            mr={60}
                                            disabled
                                        />
                                    </Row>
                                )}
                                <Row className="mb-[34px]">
                                    <ColTitleAndTextInput
                                        title="회당 가격 (원)"
                                        numeric
                                        isMoney
                                        allowDecimal
                                        value={pricePerClass}
                                        onChange={(value) => {
                                            if (value === '') {
                                                value = '0';
                                            }
                                            setPricePerClass(parseInt(value));
                                        }}
                                        width={240}
                                        height={36}
                                        onClear={() => {
                                            setPricePerClass(0);
                                        }}
                                        textInputRightIcon={false}
                                        isClearIcon={false}
                                        mr={60}
                                    />
                                    <ColTitleAndTextInput
                                        title="판매 금액 (원)"
                                        numeric
                                        isMoney
                                        allowDecimal
                                        value={totalPrice}
                                        onChange={(value) => {
                                            if (value === '') {
                                                value = '0';
                                            }
                                            setTotalPrice(parseInt(value));
                                        }}
                                        width={240}
                                        height={36}
                                        onClear={() => {
                                            setTotalPrice(0);
                                        }}
                                        textInputRightIcon={false}
                                        isClearIcon={false}
                                    />
                                </Row>
                                <Row className=" items-end mb-[34px]">
                                    <ColTitleAndTextInput
                                        title="수강권 기간"
                                        value={membershipPeriod.toString()}
                                        onChange={(value) => {
                                            if (value === '') {
                                                value = '0';
                                            }
                                            const days = paymentMethod === 'rappoPay'
                                                ? Math.min(parseInt(value), 180)
                                                : parseInt(value);
                                            setMembershipPeriod(days);
                                            setExpirationDate(
                                                dayjs(startDate).add(days, 'day').toISOString()
                                            );
                                        }}
                                        width={140}
                                        height={36}
                                        mr={32}
                                        isClearIcon={false}
                                    />
                                    <Col>
                                        <CaptionLight className="text-WHITE_900">
                                            시작, 만료날짜를 입력하시면 유효기간이 자동으로 계산 되어요!
                                        </CaptionLight>
                                        <CaptionLight className="text-WHITE_900">
                                            만료 날짜가 지나도 수업 회차가 남으면 만료되지 않아요!
                                        </CaptionLight>
                                    </Col>
                                </Row>
                                <Row className="mb-[34px]">
                                    <ColTitleAndTextInput
                                        title="시작 날짜"
                                        value={startDate}
                                        isCalendar
                                        onClickDate={(date) => {
                                            setStartDate(dayjs(date).toISOString());
                                            setMembershipPeriod(dayjs(expirationDate).diff(dayjs(date), 'day'));
                                        }}
                                        width={240}
                                        height={36}
                                        mr={60}
                                    />
                                    <Col>
                                        <ColTitleAndTextInput
                                            title="만료 날짜"
                                            value={expirationDate}
                                            isCalendar
                                            onClickDate={(date) => {
                                                const maxDate = paymentMethod === 'rappoPay'
                                                    ? dayjs(startDate).add(180, 'day').toDate()
                                                    : null;
                                                const clamped = maxDate && dayjs(date).isAfter(maxDate) ? maxDate : date;
                                                setExpirationDate(dayjs(clamped).toISOString());
                                                setMembershipPeriod(dayjs(clamped).diff(dayjs(startDate), 'day'));
                                            }}
                                            width={240}
                                            height={36}
                                        />
                                        {paymentMethod === 'rappoPay' && (
                                            <CaptionLight className="text-[#3D73DD] mt-[6px]">
                                                라포페이 결제시 만료일은 최대 6개월이에요
                                            </CaptionLight>
                                        )}
                                    </Col>
                                </Row>
                                <Row className="mb-[34px]">
                                    <ColTitleAndTextInput
                                        title="시작 회차"
                                        value={startSession.toString()}
                                        onChange={(value) => {
                                            if (value === '') {
                                                value = '0';
                                            }
                                            setStartSession(parseInt(value));
                                        }}
                                        width={140}
                                        height={36}
                                        mr={60}
                                        textInputRightIcon={false}
                                        isClearIcon={false}
                                    />
                                    <ColTitleAndTextInput
                                        title="총 회차"
                                        value={totalSession.toString()}
                                        onChange={(value) => {
                                            if (value === '') {
                                                value = '0';
                                            }
                                            setTotalSession(parseInt(value));
                                        }}
                                        width={140}
                                        height={36}
                                        textInputRightIcon={false}
                                        isClearIcon={false}
                                        mr={60}
                                    />
                                    {!isGroup && (
                                        <ColTitleAndTextInput
                                            title="수업 시간"
                                            value={classTime}
                                            onChange={(value) => {
                                                if (value === '') {
                                                    value = '0';
                                                }
                                                setClassTime(parseInt(value));
                                            }}
                                            width={140}
                                            height={36}
                                            textInputRightIcon={false}
                                            isClearIcon={false}
                                        />
                                    )}
                                </Row>
                                {/* 취소 정책 override — 그룹 전용 */}
                                {isGroup && (
                                    <Col className="mb-[34px]">
                                        <Row className="items-center mb-[8px] gap-[6px]">
                                            <Body3Regular>취소 정책</Body3Regular>
                                            <CaptionLight color={colors.LAVEL_1}>
                                                · 기본 총 {selectedGroupTicket?.cancellableCount ? `${selectedGroupTicket.cancellableCount}회` : '무제한'}
                                                {' / '}당일 {selectedGroupTicket?.sameDayCancelCount ? `${selectedGroupTicket.sameDayCancelCount}회` : '무제한'}
                                            </CaptionLight>
                                        </Row>
                                        <Row style={{ gap: 8, marginBottom: 10 }}>
                                            <button type="button"
                                                onClick={() => {
                                                    setUseCustomCancel(false);
                                                    setCancellableCountOverride(undefined);
                                                    setSameDayCancelCountOverride(undefined);
                                                }}
                                                style={{
                                                    padding: '8px 14px', borderRadius: 10, fontSize: 13,
                                                    cursor: 'pointer',
                                                    border: !useCustomCancel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                                    backgroundColor: !useCustomCancel ? '#EEF2FF' : 'white',
                                                    color: !useCustomCancel ? '#4338CA' : '#6B7280',
                                                    fontWeight: !useCustomCancel ? 600 : 400,
                                                }}>
                                                기본값 사용
                                            </button>
                                            <button type="button"
                                                onClick={() => {
                                                    setUseCustomCancel(true);
                                                    // placeholder와 동일한 초기값(템플릿 기본) 세팅 — 사용자가 바로 수정 가능
                                                    if (cancellableCountOverride == null) {
                                                        setCancellableCountOverride(selectedGroupTicket?.cancellableCount ?? 0);
                                                    }
                                                    if (sameDayCancelCountOverride == null) {
                                                        setSameDayCancelCountOverride(selectedGroupTicket?.sameDayCancelCount ?? 0);
                                                    }
                                                }}
                                                style={{
                                                    padding: '8px 14px', borderRadius: 10, fontSize: 13,
                                                    cursor: 'pointer',
                                                    border: useCustomCancel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                                    backgroundColor: useCustomCancel ? '#EEF2FF' : 'white',
                                                    color: useCustomCancel ? '#4338CA' : '#6B7280',
                                                    fontWeight: useCustomCancel ? 600 : 400,
                                                }}>
                                                이 회원만 다르게 설정
                                            </button>
                                        </Row>
                                        {useCustomCancel && (
                                            <Col style={{ gap: 8 }}>
                                                <Row style={{ gap: 12 }}>
                                                    <Col style={{ flex: 1, gap: 4 }}>
                                                        <span style={{ fontSize: 12, color: '#6B7280' }}>총 취소 허용 횟수</span>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            value={cancellableCountOverride ?? ''}
                                                            placeholder={`기본 ${selectedGroupTicket?.cancellableCount ?? 0}회`}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setCancellableCountOverride(v === '' ? undefined : Math.max(0, Number(v)));
                                                            }}
                                                            style={{ border: '1px solid #E1E4E6', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' }}
                                                        />
                                                    </Col>
                                                    <Col style={{ flex: 1, gap: 4 }}>
                                                        <span style={{ fontSize: 12, color: '#6B7280' }}>당일 취소 허용 횟수</span>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            value={sameDayCancelCountOverride ?? ''}
                                                            placeholder={`기본 ${selectedGroupTicket?.sameDayCancelCount ?? 0}회`}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setSameDayCancelCountOverride(v === '' ? undefined : Math.max(0, Number(v)));
                                                            }}
                                                            style={{ border: '1px solid #E1E4E6', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' }}
                                                        />
                                                    </Col>
                                                </Row>
                                                {cancellableCountOverride != null && sameDayCancelCountOverride != null
                                                    && sameDayCancelCountOverride > cancellableCountOverride && (
                                                    <span style={{ fontSize: 12, color: '#B91C1C' }}>
                                                        ⚠ 당일 취소 횟수는 총 취소 횟수보다 클 수 없어요
                                                    </span>
                                                )}
                                            </Col>
                                        )}
                                    </Col>
                                )}

                                {/* 만료 정책 — 그룹/PT 모두 지원 (회원별 override 가능) */}
                                <Col className="mb-[34px]">
                                        <Row className="items-center mb-[8px] gap-[6px]">
                                            <Body3Regular>만료 정책</Body3Regular>
                                            <CaptionLight color={colors.LAVEL_1}>
                                                · 템플릿 기본값에서 수정할 수 있어요
                                            </CaptionLight>
                                        </Row>
                                        <Row className="gap-[8px]" style={{ flexWrap: 'wrap' }}>
                                            {EXPIRY_POLICY_OPTIONS.map((opt) => {
                                                const sel = expiryPolicy === opt.value;
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setExpiryPolicy(opt.value)}
                                                        style={{
                                                            padding: '10px 16px',
                                                            borderRadius: 10,
                                                            border: sel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                                            backgroundColor: sel ? '#EEF2FF' : 'white',
                                                            color: sel ? '#4338CA' : '#6B7280',
                                                            fontSize: 13,
                                                            fontWeight: sel ? 600 : 400,
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            minWidth: 160,
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: sel ? 700 : 500 }}>{opt.label}</div>
                                                        <div style={{ fontSize: 11, color: sel ? '#6366F1' : '#9CA3AF', marginTop: 2 }}>
                                                            {opt.desc}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </Row>
                                    </Col>
                                <Row className="mb-[48px]">
                                    <Col className="mr-[60px]">
                                        <Body3Regular className="mb-[8px]">판매담당자</Body3Regular>
                                        {!isLoading && salesManager && (
                                            <Dropdown
                                                options={salesManager}
                                                bgColor="white"
                                                className="w-[140px] h-[36px] border border-solid border-[#D4DBE8] justify-between"
                                                borderRadius={50}
                                                arrowColor={colors.primary}
                                                onSelect={(item) => {
                                                    setSelectedSalesManager(item.object ?? undefined);
                                                }}
                                                initialSelected={initialSalesManager?.code ?? salesManager[0].code}
                                                isAllOption={false}
                                            />
                                        )}
                                    </Col>
                                    <Col className="mr-[60px]">
                                        <Body3Regular className="mb-[8px]">신규 / 재등록 구분</Body3Regular>
                                        <Dropdown
                                            options={[
                                                { code: 'none', value: '선택' },
                                                { code: 'new', value: '신규' },
                                                { code: 're', value: '재등록' },
                                            ]}
                                            initialSelected={memberType}
                                            bgColor="white"
                                            className={`w-[140px] h-[36px]  justify-between 
                                                ${
                                                    isRequiredError
                                                        ? 'border border-solid border-red'
                                                        : 'border border-solid border-[#D4DBE8]'
                                                }`}
                                            borderRadius={50}
                                            arrowColor={colors.primary}
                                            isAllOption={false}
                                            onSelect={(item) => {
                                                setMemberType(item.code);
                                            }}
                                        />
                                    </Col>
                                    <Col onClick={() => setIsCalendarOpen(true)} className="relative">
                                        <Body3Regular color={colors.LAVEL_4} className="mb-[8px]">
                                            결제일자
                                        </Body3Regular>
                                        <Col
                                            className={`w-[150px] h-[36px] border border-solid border-[#D4DBE8] pl-[16px] justify-center  rounded-full cursor-pointer`}
                                        >
                                            <Body3Regular color={colors.LAVEL_4}>
                                                {dayjs(paymentDate).format('YYYY-MM-DD')}
                                            </Body3Regular>
                                        </Col>
                                    </Col>
                                </Row>
                                <Col>
                                    <Body3Regular className="mb-[6px]">메모</Body3Regular>
                                    <CaptionLight className="mb-[8px]">
                                        수강권의 메모는 관리자님과 강사님만 확인할 수 있어요!
                                    </CaptionLight>
                                    <MemoTextInput
                                        value={memo}
                                        onChange={(e) => setMemo(e.target.value)}
                                        height={136}
                                        placeholder="기억해야 할 회원님의 상태를 메모해보세요"
                                    />
                                </Col>
                            </Col>
                        </Row>
                        {canBundleFacility && (
                            <MembershipBundleFacilitySection
                                ref={bundleRef}
                                centerId={effectiveCenterId as string}
                                membershipStartDate={startDate}
                                membershipEndDate={expirationDate}
                                membershipPaymentDate={dayjs(paymentDate).toISOString()}
                                createAlert={createAlert}
                            />
                        )}
                        <Row className="w-full justify-end">
                            <NewWhiteSquareButton
                                text="이전"
                                fontType="Body2Regular"
                                width={148}
                                height={48}
                                mr={16}
                                onClick={() => setStep(1)}
                            />
                            <NewColorSquareButton
                                text={isSubmitting ? '등록 중...' : '등록 완료'}
                                fontType="Body2Regular"
                                width={148}
                                height={48}
                                disabled={isSubmitting}
                                onClick={onClickCreateMembership}
                            />
                        </Row>
                        {isCalendarOpen && (
                            <NewCalendar
                                date={paymentDate || dayjs(paymentDate)}
                                setIsCalendar={setIsCalendarOpen}
                                onClickDate={(day) => {
                                    setPaymentDate(dayjs(day).toISOString());
                                }}
                                isAllDate={false}
                                top={200}
                                left={400}
                                selectedDay={dayjs(paymentDate)}
                                isNextMonth
                                isPrevMonth
                                titleFormat="YYYY년 MM월"
                            />
                        )}
                    </>
                )}
            </Col>
            {isMembershipNameModalVisible && (
                <ModalUpdateMembershipName
                    onUpdateComplete={(value: string) => {
                        setMembershipName(value);
                        setIsMembershipNameModalVisible(false);
                    }}
                    setIsVisible={setIsMembershipNameModalVisible}
                    value={membershipName}
                />
            )}
            {isSalesManagerModalVisible && (
                <ModalSalesManagerName
                    setIsVisible={setIsSalesManagerModalVisible}
                    onClickConfirm={(name: string) => {
                        postSalesManagerMutation.mutate({
                            membershipId: authObject.centerId,
                            managerName: name,
                        });
                    }}
                />
            )}
            {isMemberTypeModalVisible && <ModalAlertMemberType setIsVisible={setIsMemberTypeModalVisible} />}
            {isPaymentMethodBranchEnabled && paymentMethod === 'rappoPay' && step === 2 && isPreviewVisible && (
                <KakaoAlimtalkPreview
                    anchorRect={rappoPayAnchorRect}
                    centerName={authObject?.centerName}
                    memberName={memberName}
                />
            )}
        </Modal>
    );
};

export default ModalMembershipCreate;

// 카카오 알림톡 미리보기 (라포페이 결제요청 선택 시 화면 우측에 fixed 표시)
export const KakaoAlimtalkPreview = ({
    anchorRect,
    centerName,
    memberName,
}: {
    anchorRect: { top: number; left: number; right: number; bottom: number; width: number; height: number } | null;
    centerName?: string;
    memberName?: string;
}) => {
    if (!anchorRect) return null;
    const todayLabel = new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    }).format(new Date());
    const currentTimeLabel = new Intl.DateTimeFormat('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date());
    const displayCenterName = `${centerName} 센터` || '센터';
    const displayMemberName = memberName || '회원';

    const previewWidth = 254; // 꼬리(14) + 폰(240)
    const previewHeight = 430;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 버튼 우측에서 시작하되, 화면 밖으로 나가면 가능한 범위로 클램프
    const preferredLeft = anchorRect.right + 8;
    const left = Math.min(preferredLeft, viewportWidth - previewWidth - 12);

    // 버튼의 세로 중앙 기준으로 미리보기 배치
    const anchorCenterY = anchorRect.top + anchorRect.height / 2;
    const preferredTop = anchorCenterY - previewHeight / 2;
    // 모달 닫기(X) 버튼과 겹치지 않도록 미리보기를 조금 아래로 이동
    const previewOffsetY = 28;
    const top = Math.max(12, Math.min(preferredTop + previewOffsetY, viewportHeight - previewHeight - 12));

    // 꼬리가 버튼 중심 높이에서 시작하도록 위치 계산
    const tailTop = Math.max(14, Math.min(anchorCenterY - top - 9, previewHeight - 14));

    return ReactDOM.createPortal(
        <div
            style={{
                position: 'fixed',
                left,
                top,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'flex-start',
                filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.18))',
            }}
        >
            {/* 말풍선 꼬리 */}
            <div style={{ position: 'relative', width: 14, height: previewHeight, flexShrink: 0 }}>
                <div
                    style={{
                        position: 'absolute',
                        top: tailTop,
                        left: 0,
                        width: 0,
                        height: 0,
                        borderTop: '9px solid transparent',
                        borderBottom: '9px solid transparent',
                        borderRight: '14px solid #d1d5db',
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        top: tailTop + 1,
                        left: 2,
                        width: 0,
                        height: 0,
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderRight: '13px solid #f9f9f9',
                        zIndex: 1,
                    }}
                />
            </div>

            {/* 모바일 폰 프레임 */}
            <div
                style={{
                    width: 240,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 28,
                    border: '2px solid #d1d5db',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                }}
            >
                {/* 폰 상단 상태바 */}
                <div
                    style={{
                        backgroundColor: '#1c1c1e',
                        padding: '7px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ color: 'white', fontSize: 10, fontWeight: 600 }}>{currentTimeLabel}</span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ color: 'white', fontSize: 9 }}>🔋</span>
                    </div>
                </div>

                {/* 채팅 배경 */}
                <div
                    style={{
                        backgroundColor: '#ABC1D1',
                        padding: '14px 10px',
                        minHeight: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    {/* 날짜 표시 */}
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                        <span
                            style={{
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                color: 'white',
                                fontSize: 9,
                                padding: '2px 8px',
                                borderRadius: 10,
                            }}
                        >
                            {todayLabel}
                        </span>
                    </div>

                    {/* 알림톡 메시지 */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        {/* 라포 프로필 아이콘 */}
                        <img
                            src={require('../../assets/images/common/i_rappo_logo.png')}
                            alt="Rappo"
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1.5px solid white',
                            }}
                        />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 10, color: '#1a1a1a', fontWeight: 600 }}>Rappo</span>

                            {/* 알림톡 버블 */}
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '0 10px 10px 10px',
                                    padding: '10px 11px',
                                    maxWidth: 172,
                                    border: '1px solid #e0e0e0',
                                }}
                            >
                                {/* 알림톡 배지 */}
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 3,
                                        backgroundColor: '#FAE100',
                                        borderRadius: 3,
                                        padding: '2px 6px',
                                        marginBottom: 8,
                                    }}
                                >
                                    <span style={{ fontSize: 8, fontWeight: 700, color: '#3C1E1E' }}>알림톡</span>
                                </div>

                                <p style={{ fontSize: 10, color: '#1a1a1a', lineHeight: 1.6, margin: 0, marginBottom: 6 }}>
                                    안녕하세요, <strong>{displayMemberName}</strong>님!
                                </p>
                                <p style={{ fontSize: 10, color: '#333', lineHeight: 1.6, margin: 0, marginBottom: 8 }}>
                                    <strong>{displayCenterName}</strong>에서<br /> 라포페이를 통하여 안전한<br /> 온라인 수강권 결제를 요청드립니다.
                                </p>

                                <div
                                    style={{
                                        borderTop: '1px solid #f0f0f0',
                                        borderBottom: '1px solid #f0f0f0',
                                        paddingTop: 7,
                                        paddingBottom: 7,
                                        marginBottom: 7,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 9, color: '#888' }}>수강권명</span>
                                        <span style={{ fontSize: 9, color: '#1a1a1a', fontWeight: 600 }}>[수강권명]</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 9, color: '#888' }}>결제금액</span>
                                        <span style={{ fontSize: 9, color: '#1a1a1a', fontWeight: 600 }}>[금액]원</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 9, color: '#888' }}>유효기간</span>
                                        <span style={{ fontSize: 9, color: '#1a1a1a', fontWeight: 600 }}>[기간]</span>
                                    </div>
                                </div>

                                <p style={{ fontSize: 9, color: '#999', margin: 0, marginBottom: 8, lineHeight: 1.5 }}>
                                    아래 버튼을 눌러 결제를 완료해주세요.
                                </p>

                                {/* 결제하기 버튼 */}
                                <div
                                    style={{
                                        backgroundColor: '#FAE100',
                                        borderRadius: 5,
                                        padding: '7px 0',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        border: '1px solid #e6cf00',
                                    }}
                                >
                                    <span style={{ fontSize: 10, fontWeight: 700, color: '#3C1E1E' }}>결제하기</span>
                                </div>
                            </div>

                            <span style={{ fontSize: 9, color: '#4a4a4a' }}>오전 10:30</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// 회원 구분 경고창 모달
export const ModalAlertMemberType = ({ setIsVisible }: { setIsVisible: Dispatch<SetStateAction<boolean>> }) => {
    return (
        <Modal whiteViewHeight={189} whiteViewWidth={495} setIsVisible={setIsVisible} isCloseOutside={false}>
            <Col className="flex  h-full px-[33px] py-[36px]">
                <BoldText fontSize={20} lineHeight={28} className="mb-[16px]">
                    신규/재등록 구분을 선택해주세요!
                </BoldText>
                <Col
                    className="justify-center items-center bg-primary rounded-[13px] absolute bottom-[36px] right-[33px] px-[35px] py-[10px] cursor-pointer"
                    onClick={() => setIsVisible(false)}
                >
                    <RegularText fontSize={16} lineHeight={24} className="text-center" color="white">
                        확인
                    </RegularText>
                </Col>
            </Col>
        </Modal>
    );
};
